import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { BetterRequestService } from '@services';

import { CoreService } from '../../../../core/core.service';
import { IUserCostcenter } from '../../../../core/environment.interfaces';
import { EnvironmentService } from '../../../../core/environment.service';
import { IRole, IUserAuthorization, IUserBasedata } from './user.model';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private http = inject(HttpClient);
    private readonly betterRequestService = inject(BetterRequestService);
    private environmentService = inject(EnvironmentService);
    private coreService = inject(CoreService);

    getUserList(): Observable<IUserBasedata[]> {
        // this.userList=[];
        let url = this.environmentService.getURL(this.environmentService.appSettings.fakeServer) + '/usermanagement/list';
        if (!this.environmentService.appSettings.fakeServer) {
            url = url + '?locked=ALL';
            url = url + '&language=' + this.environmentService.appSettings.locale;
        }
        return this.betterRequestService
            .withoutErrorHandler(
                this.http.get<IUserBasedata[]>(url, {
                    headers: this.environmentService.appSettings.headers,
                    observe: 'response',
                }),
            )
            .pipe(
                map(
                    (response) =>
                        // this.userList = response.body;
                        response.body as IUserBasedata[],
                ),
                catchError(this.coreService.handleError),
            );
    }

    searchUserList(searchvalue: string, searchposition?: string, locked?: string): Observable<IUserBasedata[]> {
        if (!searchposition) searchposition = 'MIDDLE';
        if (!locked) locked = 'ALL';
        const url = this.environmentService.getURL(this.environmentService.appSettings.fakeServer) + '/usermanagement/search';
        let params = new HttpParams();
        if (!this.environmentService.appSettings.fakeServer) {
            params = params
                .set('locked', locked)
                .set('language', this.environmentService.appSettings.locale)
                .set('search', searchvalue)
                .set('searchposition', searchposition);
        }
        return this.betterRequestService
            .withoutErrorHandler(
                this.http.get<IUserBasedata[]>(url, {
                    params: params,
                    headers: this.environmentService.appSettings.headers,
                    observe: 'response',
                }),
            )
            .pipe(
                map((response) => response.body as IUserBasedata[]),
                catchError(this.coreService.handleError),
            );
    }

    getUser() {
        let url = this.environmentService.getURL(this.environmentService.appSettings.fakeServer) + '/usermanagement/get/self';
        if (!this.environmentService.appSettings.fakeServer) {
            url = url + '?language=' + this.environmentService.appSettings.locale;
        }
        return this.betterRequestService
            .withoutErrorHandler(
                this.http.get<IUserBasedata>(url, {
                    headers: this.environmentService.appSettings.headers,
                    observe: 'response',
                }),
            )
            .pipe(
                map((response) => response.body as IUserBasedata),
                catchError(this.coreService.handleError),
            );
    }
    getUserById(userId: number): Observable<IUserBasedata> {
        // this.userList=[];
        let url = this.environmentService.getURL(this.environmentService.appSettings.fakeServer) + '/usermanagement/get';
        if (!this.environmentService.appSettings.fakeServer) {
            url = url + '?userId=' + userId;
            url = url + '&language=' + this.environmentService.appSettings.locale;
        }
        return this.betterRequestService
            .withoutErrorHandler(
                this.http.get<IUserBasedata>(url, {
                    headers: this.environmentService.appSettings.headers,
                    observe: 'response',
                }),
            )
            .pipe(
                map((response) => response.body as IUserBasedata),
                catchError(this.coreService.handleError),
            );
    }

    saveUser(userBasedata: IUserBasedata): Observable<IUserBasedata> {
        // delete userBasedata.lockLogin;
        // delete userBasedata.lockLoginStamp;
        // delete userBasedata.lockLoginCount;
        // delete userBasedata.lockLoginFreeUser;
        // delete userBasedata.lockLoginFreeStamp;
        // delete userBasedata.loginX509thumbprint;

        let url =
            this.environmentService.getURL(this.environmentService.appSettings.fakeServer) +
            (userBasedata.id ? '/usermanagement/edit' : '/usermanagement/create');
        if (!this.environmentService.appSettings.fakeServer) {
            url = url + '?language=' + this.environmentService.appSettings.locale;
        }
        return this.betterRequestService
            .withoutErrorHandler(
                this.http.post<IUserBasedata>(encodeURI(url), userBasedata, {
                    headers: this.environmentService.appSettings.headers,
                    observe: 'response',
                }),
            )
            .pipe(
                map((response) => response.body as IUserBasedata),
                catchError(this.coreService.handleError),
            );
    }

    saveUserCostcenter(userCostcenter: IUserCostcenter) {
        const url = this.environmentService.getURL(this.environmentService.appSettings.fakeServer) + '/usercostcenter/edit';
        return this.betterRequestService
            .withoutErrorHandler(
                this.http.post<IUserCostcenter>(encodeURI(url), userCostcenter, {
                    headers: this.environmentService.appSettings.headers,
                    observe: 'response',
                }),
            )
            .pipe(
                map((response) => true),
                catchError(this.coreService.handleError),
            );
    }

    getRoles(): Observable<IRole[]> {
        let url = this.environmentService.getURL(this.environmentService.appSettings.fakeServer) + '/role/list';
        if (!this.environmentService.appSettings.fakeServer) {
            url = url + '?locked=FALSE';
            url = url + '&deleted=FALSE';
            url = url + '&language=' + this.environmentService.appSettings.locale;
        }
        return this.betterRequestService
            .withoutErrorHandler(
                this.http.get<IRole[]>(url, {
                    headers: this.environmentService.appSettings.headers,
                    observe: 'response',
                }),
            )
            .pipe(
                map((response) => response.body as IRole[]),
                catchError(this.coreService.handleError),
            );
    }

    getUserAuthorization(userId: number): Observable<IUserAuthorization> {
        let url = this.environmentService.getURL(this.environmentService.appSettings.fakeServer) + '/userauthorization/get';
        if (!this.environmentService.appSettings.fakeServer) {
            url = url + '?userId=' + userId;
            url = url + '&language=' + this.environmentService.appSettings.locale;
        }
        return this.betterRequestService
            .withoutErrorHandler(
                this.http.get<IUserAuthorization>(url, {
                    headers: this.environmentService.appSettings.headers,
                    observe: 'response',
                }),
            )
            .pipe(
                map((response) => response.body as IUserAuthorization),
                catchError(this.coreService.handleError),
            );
    }

    saveUserAuthorization(userAuthorization: IUserAuthorization) {
        userAuthorization.userroles.forEach((ur) => {
            delete ur['_descr'];
            delete ur['_subRolesDescr'];
        });
        let url = this.environmentService.getURL(this.environmentService.appSettings.fakeServer) + '/userauthorization/edit';
        if (!this.environmentService.appSettings.fakeServer) {
            url = url + '?language=' + this.environmentService.appSettings.locale;
        }
        return this.betterRequestService
            .withoutErrorHandler(
                this.http.post<IUserAuthorization>(encodeURI(url), userAuthorization, {
                    headers: this.environmentService.appSettings.headers,
                    observe: 'response',
                }),
            )
            .pipe(
                map((response) => true),
                catchError(this.coreService.handleError),
            );
    }
}
